/**
 * Reading the machine-readable code out of an error response.
 *
 * Every Baserow error body is `{ error, detail }` where `error` is a stable
 * `ERROR_*` string. Kept apart from `ApiError` because the middleware needs the
 * code off a `Response` before anything has been unwrapped, and pulling in the
 * error class -- and through it the whole message table and i18n -- for that is
 * both circular and unnecessary.
 */

export interface ErrorBody {
  code: string | null
  detail: unknown
}

export function parseErrorBody(body: unknown): ErrorBody {
  if (typeof body !== 'object' || body === null) {
    return { code: null, detail: body }
  }
  const record = body as Record<string, unknown>
  return {
    code: typeof record['error'] === 'string' ? record['error'] : null,
    detail: record['detail'] ?? null,
  }
}

/**
 * The `ERROR_*` code of a response, or null.
 *
 * Consumes the response, so callers holding one they still need must pass a
 * `clone()`. A body that is not JSON is not an error here: a 502 from a proxy in
 * front of the backend is HTML, and it simply has no code.
 */
export async function readErrorCode(response: Response): Promise<string | null> {
  try {
    return parseErrorBody(await response.json()).code
  } catch {
    return null
  }
}
